import React from "react";
import { Axis, Cell } from "../../actions/crmScoring";
import { getSegmentZone, PERSONAS } from "./personas";
import { alpha, Box, Button, Group, SimpleGrid, Stack, Text, Title, UnstyledButton } from "@mantine/core";
import RankExplanation from "../RankExplanation";

interface Props {
  axis: Axis
  cells: Cell[]
  selectedCells: Array<[number, number]>
  onToggleCell: (x: number, y: number) => void
  onClearSelectedCells: () => void
}

const MAIN_COLOR = 'var(--mantine-color-invy-6)'

const labelsFor = (axis: Axis) => {
  if (axis === 'r2_f2') {
    return {
      title: '紹介アクティブ度・マトリクス',
      description: '最終紹介日（R2）と紹介行動数（F2）から、ユーザーの活動状態を分類します。',
      x: { title: '最終紹介日（R2ランク）', items: ['365日～', '91～364', '61～90', '31～60', '0～30'] },
      y: { title: '紹介行動数（F2ランク）', items: ['1回', '2回', '3回', '4～5回', '6回以上'] },
    }
  }

  return {
    title: '紹介インフルエンス・マトリクス',
    description: '紹介行動数（F2）と成果数（M2）から、ユーザーの影響力タイプを4つのペルソナに分類します。',
    description2: 'セルをクリックしてターゲットを絞り込み、最適な施策を実行してください。',
    x: { title: 'ゲストCV数（M2ランク）', items: ['0回', '1回', '2～3回', '4～5回', '6回以上'] },
    y: { title: '紹介行動数（F2ランク）', items: ['1回', '2回', '3回', '4～5回', '6回以上'] },
  }
}

const ScoringMatrix: React.FC<Props> = ({ axis, cells, selectedCells, onToggleCell, onClearSelectedCells }) => {
  const labels = labelsFor(axis)
  const cellMap = new Map<string, number>()
  cells.forEach((c) => cellMap.set(`${c.x},${c.y}`, c.count))

  const isSelected = (x: number, y: number) =>
    selectedCells.some(([cx, cy]) => cx === x && cy === y)

  // y軸：上=ランク5、下=ランク1。x軸：左=ランク1、右=ランク5
  const ys = [5, 4, 3, 2, 1]
  const xs = [1, 2, 3, 4, 5]

  // セル背景色：F2×M2はペルソナ色のグラデーション、R2×F2はメインカラー
  // データに依存するため inline style で指定
  const cellColor = (x: number, y: number) => {
    if (axis === 'f2_m2') {
      // F2×M2では：x=M2, y=F2
      const f2 = y
      const m2 = x
      const seg = getSegmentZone(f2, m2)
      const intensity = 0.3 + ((f2 + m2 - 2) / 8) * 0.5
      return { bg: `rgba(${seg.rgb}, ${intensity.toFixed(3)})`, border: `rgba(${seg.rgb}, .8)` }
    }
    const op = Math.max(0.2, (x + y - 2) / 8)
    return { bg: `rgba(222, 91, 91, ${op.toFixed(3)})`, border: `rgba(222, 91, 91, .8)` }
  }

  return (
    <Box>
      {/* ヘッダー：左にタイトル+説明、右にペルソナ凡例（2×2） */}
      <Group justify="space-between" align="flex-start" gap={24} mb={32}>
        <Box style={{ flex: 1, minWidth: 280 }}>
          <Title order={2} size={18} fw={600} mb={8}>
            {labels.title}
          </Title>
          <Text fz={13} c="#00000099">
            {labels.description}
            {axis === 'f2_m2' && labels.description2 && (
              <>
                <br />
                {labels.description2}
              </>
            )}
          </Text>
          <RankExplanation />
        </Box>

        {axis === 'f2_m2' && (
          <Box bg="#00000008" p={16} bdrs={8} miw={320}>
            <SimpleGrid cols={2} spacing={12}>
              {PERSONAS.map((p) => (
                <Group key={p.name} gap={8} align="center" wrap="nowrap">
                  <Box
                    w={12}
                    h={12}
                    style={{ background: `rgb(${p.rgb})`, borderRadius: '50%', flexShrink: 0 }}
                  />
                  <Box>
                    <Text fz={12} fw={600} c="#000000CC">{p.name}</Text>
                    <Text fz={10} c="#00000099">{p.description}</Text>
                  </Box>
                </Group>
              ))}
            </SimpleGrid>
          </Box>
        )}
      </Group>

      {/* 選択中セルの状態表示・クリアボタン */}
      {selectedCells.length > 0 && (
        <Group
          justify="space-between"
          bg={alpha(MAIN_COLOR, 0.08)}
          bdrs={6}
          px={12}
          py={8}
          mb={12}
          style={{ border: `1px solid ${alpha(MAIN_COLOR, 0.4)}` }}
        >
          <Text fz={13} c="#000000CC">
            現在 <Text span fw={600} fz={15} c={MAIN_COLOR} mx={2}>{selectedCells.length}</Text> 個のセルを選択中
          </Text>
          <Button
            variant="outline"
            color={MAIN_COLOR}
            radius="xl"
            size="xs"
            onClick={onClearSelectedCells}
          >
            選択をクリア
          </Button>
        </Group>
      )}

      {/* マトリクス本体 */}
      <Box
        pt={12}
        px={12}
        pb={16}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', overflowX: 'auto' }}
      >
        <Box style={{ display: 'flex', minWidth: 'max-content' }}>
          {/* 縦軸ラベル */}
          <Stack justify="space-between" align="center" gap={0} mr={16} py={32}>
            <Text fz={12} c="#00000099">多 ↑</Text>
            <Text fz={13} c="#00000099" style={{ writingMode: 'vertical-rl', letterSpacing: 3 }}>
              {labels.y.title}
            </Text>
            <Text fz={12} c="#00000099">↓ 少</Text>
          </Stack>

          {/* グリッド */}
          <Stack gap={8}>
            {ys.map((y) => (
              <Group key={y} gap={8} wrap="nowrap">
                <Text w={64} ta="right" fz={12} c="#00000099" mr={8}>
                  {labels.y.items[y - 1]}
                </Text>
                <Group gap={8} wrap="nowrap">
                  {xs.map((x) => {
                    const count = cellMap.get(`${x},${y}`) ?? 0
                    const sel = isSelected(x, y)
                    const colors = cellColor(x, y)
                    if (count === 0) {
                      return (
                        <Box
                          key={x}
                          w={96}
                          h={96}
                          bdrs={8}
                          bg="#00000008"
                          style={{ border: '2px solid transparent', pointerEvents: 'none' }}
                        />
                      )
                    }
                    return (
                      <UnstyledButton
                        key={x}
                        className={`crm-matrix-cell${sel ? ' is-selected' : ''}`}
                        w={96}
                        h={96}
                        bdrs={8}
                        onClick={() => onToggleCell(x, y)}
                        style={{
                          background: colors.bg,
                          borderColor: sel ? MAIN_COLOR : colors.border,
                        }}
                      >
                        {sel && (
                          <Box
                            aria-hidden="true"
                            w={18}
                            h={18}
                            bg={MAIN_COLOR}
                            style={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              borderRadius: '50%',
                              color: '#fff',
                              fontSize: 12,
                              lineHeight: '18px',
                              textAlign: 'center',
                              fontWeight: 700,
                            }}
                          >
                            ✓
                          </Box>
                        )}
                        <Text fz={24} fw={700} lh={1} c="#000000CC">{count}</Text>
                        <Text fz={10} c="#00000099" mt={4}>名</Text>
                      </UnstyledButton>
                    )
                  })}
                </Group>
              </Group>
            ))}

            {/* 横軸ラベル */}
            <Group gap={8} mt={12} ml={80} wrap="nowrap">
              {labels.x.items.map((label) => (
                <Text key={label} w={96} ta="center" fz={12} c="#00000099">{label}</Text>
              ))}
            </Group>
          </Stack>
        </Box>

        {/* 横軸タイトル */}
        <Group justify="center" align="center" gap={0} mt={24} pl={48}>
          <Text fz={12} c="#00000099" mr={32}>少 ←</Text>
          <Text fz={13} c="#00000099" style={{ letterSpacing: 3 }}>{labels.x.title}</Text>
          <Text fz={12} c="#00000099" ml={32}>→ 多</Text>
        </Group>
      </Box>
    </Box>
  )
}

export default ScoringMatrix
